import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { request } from "app/network";
import Swal from "sweetalert2";
import { useQuery } from "@tanstack/react-query";

import MDInput from "components/MDInput"; // Import MDInput

interface EditDisplayItemDialogProps {
    open: boolean;
    onClose: () => void;
    item: any;
    onSuccess: () => void;
    darkMode?: boolean;
}

const EditDisplayItemDialog = ({ open, onClose, item, onSuccess, darkMode }: EditDisplayItemDialogProps) => {
    const [selectedCategory, setSelectedCategory] = useState<number | string>("");
    const [requiredPoint, setRequiredPoint] = useState<string>("");
    const [categories, setCategories] = useState<any[]>([]);

    // Fetch Categories
    const categoryQuery = useQuery({
        queryKey: ["giftCategories"],
        queryFn: async () => {
            const response = await request("GiftCategory.GetList", {});
            return response.data || [];
        },
        enabled: open,
    });

    useEffect(() => {
        if (categoryQuery.data) {
            setCategories(categoryQuery.data);
        }
    }, [categoryQuery.data]);

    useEffect(() => {
        if (open && item) {
            setSelectedCategory(item.categorySeqId || "");
            setRequiredPoint(item.requiredPoint ? String(item.requiredPoint) : (item.salePrice ? String(item.salePrice) : "0"));
        }
    }, [open, item]);

    const handleSave = async () => {
        if (!selectedCategory) {
            Swal.fire("오류", "카테고리를 선택해주세요.", "warning");
            return;
        }

        try {
            await request(`Giftishow.UpdateDisplayItem`, {
                pathParams: { displaySeq: item.displaySeq },
                paramz: {
                    categorySeqId: Number(selectedCategory),
                    requiredPoint: requiredPoint ? Number(requiredPoint) : undefined
                }
            });

            Swal.fire("성공", "수정되었습니다.", "success");
            onSuccess();
            onClose();
        } catch (error: any) {
            console.error("Update failed", error);
            Swal.fire("실패", error.message || "수정 중 오류가 발생했습니다.", "error");
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    p: 2,
                    backgroundColor: darkMode ? "#1a2035" : "#fff"
                }
            }}
        >
            <DialogTitle sx={{ color: darkMode ? '#fff' : '#344767' }}>
                <MDTypography variant="h5" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                    상품 정보 수정
                </MDTypography>
            </DialogTitle>
            <DialogContent>
                {item && (
                    <MDBox display="flex" flexDirection="column" gap={2}>
                        <MDBox display="flex" alignItems="center" gap={2} p={2} border="1px solid" borderColor={darkMode ? "rgba(255,255,255,0.1)" : "#eee"} borderRadius="lg">
                            <MDBox component="img" src={item.goodsImgS} width="80px" borderRadius="md" alt={item.goodsName} />
                            <MDBox>
                                <MDTypography variant="body2" color="text">{item.brandName}</MDTypography>
                                <MDTypography variant="h6" color={darkMode ? "white" : "dark"}>{item.goodsName}</MDTypography>
                                <MDTypography variant="body2" fontWeight="bold" color={darkMode ? "white" : "dark"}>
                                    {Number(item.salePrice).toLocaleString()}원
                                </MDTypography>
                            </MDBox>
                        </MDBox>

                        <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" mb={1} display="block" color={darkMode ? "white" : "dark"}>
                                필요 포인트 (판매가)
                            </MDTypography>
                            <MDInput
                                fullWidth
                                type="number"
                                value={requiredPoint}
                                onChange={(e: any) => setRequiredPoint(e.target.value)}
                                placeholder="포인트를 입력하세요"
                                sx={{
                                    "& input[type=number]": {
                                        colorScheme: darkMode ? "dark" : "normal",
                                    },
                                }}
                            />
                        </MDBox>

                        <MDBox>
                            <MDTypography variant="caption" fontWeight="bold" mb={1} display="block" color={darkMode ? "white" : "dark"}>
                                카테고리 선택
                            </MDTypography>
                            <FormControl fullWidth sx={{ mt: 1 }}>
                                <Select
                                    value={selectedCategory}
                                    onChange={(e) => setSelectedCategory(e.target.value)}
                                    displayEmpty
                                    inputProps={{ "aria-label": "Category Select" }}
                                    sx={{
                                        height: 45,
                                        backgroundColor: darkMode ? "#202940" : "#fff",
                                        "& .MuiOutlinedInput-notchedOutline": {
                                            borderColor: darkMode ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.12)",
                                        },
                                        "& .MuiSelect-icon": {
                                            color: darkMode ? "#ffffff !important" : "#000000 !important",
                                            display: "block !important",
                                            right: "10px",
                                            position: "absolute",
                                            fontSize: "1.5rem !important"
                                        }
                                    }}
                                >
                                    <MenuItem value="" disabled>카테고리를 선택하세요</MenuItem>
                                    {categories.map((cat) => (
                                        <MenuItem key={cat.seqId} value={cat.seqId}>{cat.categoryName}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </MDBox>
                    </MDBox>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} color="secondary">취소</Button>
                <MDButton variant="gradient" color="info" onClick={handleSave}>저장</MDButton>
            </DialogActions>
        </Dialog>
    );
};

export default EditDisplayItemDialog;
